/**
 * Nutrition calculation endpoints.
 *
 * Extracted from `src/services/api.ts` (Fase 10 Slice 10.1):
 *   - POST /calculate          — full nutrition + recipe quantities
 *   - GET  /calculate-preview  — light DER + counts for the P9 reveal
 *
 * Re-exported from `services/api.ts` to keep existing imports working.
 */

import axios from 'axios';
import { apiClient, OutOfStockDetailPayload, toOutOfStockError, withRetry } from './client';
import { DogFormData } from '../types';

/** Why a recipe ships in its resolved size (backend `SizeReason`). */
export type SizeReason = 'preferred' | 'conservation' | 'stock_fallback' | 'catalog_fallback';

/** A selected recipe the plan cannot serve in any allowed size. `recipe_id` is the
 *  selected (base) product id — the funnel key — and `recipe_group` covers its sizes. */
export interface DroppedRecipeResponse {
  recipe_id: string;
  recipe_group: string;
  name: string;
  reason: 'no_stock' | 'no_size';
}

// ─── Response shapes ────────────────────────────────────────────────────────

export interface RecipeQuantityResponse {
  recipe_id: string;
  recipe_name: string;
  protein_type: string;
  daily_grams: number;
  daily_cans: number;
  initiation_cans: number;
  monthly_cans: number;
  price_per_can: number;
  // Full external identity of the EFFECTIVE channel. A channel id is not a single
  // "variant id": PrestaShop needs the pair id_product (external_product_id) +
  // id_product_attribute (external_variant_id). On a Shopify funnel external_variant_id
  // IS the Shopify variant Appstle/Mi Cuenta keys by; both "" if the product has no
  // binding for the effective channel.
  external_product_id?: string;
  external_variant_id: string;
  resolved_size_g?: number;
  size_was_auto?: boolean;
  size_reason?: SizeReason;
}

export interface PackSummaryResponse {
  period_kind: 'monthly' | 'initiation';
  target_calendar_days: number;
  target_full_ration_equivalent_days: number;
  target_kcal: number;
  actual_total_grams: number;
  actual_total_kcal: number;
  actual_days_covered: number;
  actual_full_ration_equivalent_days: number;
  kcal_deviation_percent: number;
  fit_status: 'within_tolerance' | 'acceptable_excess' | 'acceptable_shortfall' | 'out_of_bounds_fallback';
  recipe_distribution_target: Record<string, number>;
  recipe_distribution_actual: Record<string, number>;
  recipe_distribution_deviation: Record<string, number>;
  total_units: number;
  pack_size_g: number;
  /** recipe_group → grams; pack_size_g is 0 when sizes are mixed. */
  pack_sizes_g?: Record<string, number>;
  algorithm_version: number;
  candidate_count: number;
}

export interface PackConservationResponse {
  daily_ml_grams: number;
  allowed_pack_sizes: number[];
  requested_pack_size_g: number;
  resolved_pack_size_g: number;
  open_pack_hours_400: number;
  open_pack_hours_800: number;
  warning_codes: Array<'PACK_800_NOT_ALLOWED' | 'PACK_400_OVER_72H'>;
}

export interface CalculationResponse {
  calculation_id: string;
  rer: number;
  der: number;
  daily_total_grams: number;
  diet_type: string;
  recipes: RecipeQuantityResponse[];
  initiation_pack_price: number;
  monthly_price: number;
  life_stage: 'puppy' | 'adult' | 'transition';
  estimated_adult_weight_kg: number;
  size_class: 'xs' | 's' | 'm' | 'l' | 'xl';
  factors: {
    // Claves del K-model (backend K-model).
    k_age?: number;
    k_bcs?: number;
    k_activity?: number;
    k_total?: number;
    neutered_factor_applied?: number;
    // Claves legacy que el backend sigue derivando del K-model:
    // activity_factor = k_activity x 100, body_status_delta = (k_bcs - 1) x 100.
    activity_factor: number;
    body_status_delta: number;
    senior_factor: number;
  };
  diagnostics: string[];
  monthly_pack?: PackSummaryResponse | null;
  initiation_pack?: PackSummaryResponse | null;
  pack_conservation?: PackConservationResponse | null;
  dropped_recipes?: DroppedRecipeResponse[];
}

export type CalculationPlanResponse = Omit<CalculationResponse, 'calculation_id'>;

export interface CalculationPreview {
  daily_kcal: number;
  daily_grams: number;
  offerable_recipe_count: number;
  supplement_suggestion_count: number;
}

// ─── Endpoints ──────────────────────────────────────────────────────────────

/**
 * Calcula el plan nutricional del perro.
 * Devuelve recetas, kcal/día, ración y precios.
 *
 * Errores tipados:
 * - OutOfStockError (409, code='OUT_OF_STOCK'): la selección quedó sin
 *   stock entre paso 11 y checkout. El frontend debe rebotar a step 11.
 */
export const calculateNutrition = async (dogData: DogFormData): Promise<CalculationResponse> => {
  return withRetry(async () => {
    try {
      const response = await apiClient.post<CalculationResponse>('/calculate', dogData);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error calculating nutrition:', error);
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const detailPayload = error.response?.data?.detail as OutOfStockDetailPayload | undefined;
        if (status === 409 && detailPayload?.code === 'OUT_OF_STOCK') {
          throw toOutOfStockError(detailPayload, status);
        }
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string' && detail.trim()) {
          throw new Error(`No se pudo calcular el plan nutricional: ${detail}`);
        }
      }
      throw new Error('No se pudo calcular el plan nutricional. Por favor, inténtalo de nuevo.');
    }
  });
};

/**
 * Calcula el plan nutricional del perro de forma stateless (sin persistir
 * en base de datos). Devuelve el mismo plan que `calculateNutrition` pero
 * sin `calculation_id`. Cada receta incluye `external_variant_id`.
 *
 * Usado por los pasos de selección de recetas y checkout donde se
 * necesita el plan actualizado sin crear un registro nuevo.
 *
 * Errores tipados:
 * - OutOfStockError (409, code='OUT_OF_STOCK'): la selección quedó sin
 *   stock. El frontend debe rebotar a step 11.
 */
export const calculatePlan = async (dogData: DogFormData): Promise<CalculationPlanResponse> => {
  return withRetry(async () => {
    try {
      const response = await apiClient.post<CalculationPlanResponse>('/calculate-plan', dogData);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const detailPayload = error.response?.data?.detail as OutOfStockDetailPayload | undefined;
        if (status === 409 && detailPayload?.code === 'OUT_OF_STOCK') {
          throw toOutOfStockError(detailPayload, status);
        }
        // Propagar el detalle del backend (p.ej. "recetas inválidas") en vez de
        // un genérico opaco, igual que calculateNutrition (auditoría review C1).
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string' && detail.trim()) {
          throw new Error(`No se pudo calcular el plan: ${detail}`);
        }
      }
      throw new Error('No se pudo calcular el plan. Inténtalo de nuevo.');
    }
  });
};

/**
 * Llama al endpoint ligero /calculate-preview para obtener DER, ración
 * diaria y conteos de recetas y suplementos sin necesitar
 * selectedRecipes. Usado por la pantalla P9 Reveal animado.
 *
 * Acepta un AbortSignal opcional para cancelar la request si el
 * componente se desmonta.
 */
export const calculateNutritionPreview = async (
  formData: Partial<DogFormData>,
  signal?: AbortSignal,
): Promise<CalculationPreview> => {
  return withRetry(
    async () => {
      const { data } = await apiClient.post<CalculationPreview>('/calculate-preview', formData, { signal });
      return data;
    },
    { maxRetries: 2 },
  );
};
